import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { ArrowLeft, ArrowRight, Calendar, Clock, ChevronRight, List } from "lucide-react";
import { getPostBySlug, getAdjacentPosts } from "../data/blogPosts";

interface TocItem {
  id: string;
  text: string;
  level: number;
}

function extractToc(markdown: string): TocItem[] {
  const headingRegex = /^(#{2,3})\s+(.+)$/gm;
  const items: TocItem[] = [];
  let match;
  while ((match = headingRegex.exec(markdown)) !== null) {
    const text = match[2].replace(/[`*_~]/g, "");
    const id = text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    items.push({ id, text, level: match[1].length });
  }
  return items;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[`*_~]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showToc, setShowToc] = useState(false);

  const post = slug ? getPostBySlug(slug) : undefined;
  const adjacent = slug ? getAdjacentPosts(slug) : { prev: null, next: null };

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    fetch(`/blog/${slug}.md`)
      .then((res) => {
        if (!res.ok) throw new Error("Post not found");
        return res.text();
      })
      .then((text) => {
        setMarkdown(text);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const toc = useMemo(() => (markdown ? extractToc(markdown) : []), [markdown]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" />
      </div>
    );
  }

  if (error || !markdown) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h1 className="text-2xl font-bold mb-4">Post not found</h1>
        <p className="text-text-muted mb-6">The blog post you're looking for doesn't exist.</p>
        <Link to="/blog" className="text-brand hover:underline">
          Back to all posts
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post?.title ?? "Blog"} | StarSinger MCP</title>
        <meta name="description" content={post?.excerpt ?? ""} />
        <link rel="canonical" href={`https://mcp.starsinger.ai/blog/${slug}`} />
        <meta property="og:title" content={post?.title ?? "StarSinger MCP Blog"} />
        <meta property="og:description" content={post?.excerpt ?? ""} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://mcp.starsinger.ai/blog/${slug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post?.title ?? ""} />
        <meta name="twitter:description" content={post?.excerpt ?? ""} />
      </Helmet>

      <article className="max-w-3xl mx-auto px-6 py-12">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-text-muted mb-8">
          <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          {post?.category && (
            <span className="text-xs font-medium text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded">
              {post.category}
            </span>
          )}
        </div>

        {/* Post meta */}
        {post && (
          <div className="flex items-center gap-4 text-sm text-text-muted mb-8 flex-wrap">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {post.date}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </span>
          </div>
        )}

        {/* Table of Contents toggle */}
        {toc.length > 2 && (
          <div className="mb-8 border border-border rounded-xl bg-surface-light overflow-hidden">
            <button
              onClick={() => setShowToc(!showToc)}
              className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-text-muted hover:text-white transition-colors"
            >
              <span className="flex items-center gap-2">
                <List className="w-4 h-4" />
                Table of Contents
              </span>
              <ChevronRight className={`w-4 h-4 transition-transform ${showToc ? "rotate-90" : ""}`} />
            </button>
            {showToc && (
              <nav className="px-5 pb-4">
                <ul className="space-y-1.5">
                  {toc.map((item) => (
                    <li key={item.id} style={{ paddingLeft: `${(item.level - 2) * 16}px` }}>
                      <a
                        href={`#${item.id}`}
                        className="text-sm text-text-muted hover:text-indigo-400 transition-colors block py-0.5"
                      >
                        {item.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </div>
        )}

        {/* Markdown content */}
        <div className="prose-custom">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => (
                <h1 className="text-3xl sm:text-4xl font-bold text-white mb-6 leading-tight">
                  {children}
                </h1>
              ),
              h2: ({ children }) => {
                const text = String(children);
                const id = slugify(text);
                return (
                  <h2 id={id} className="text-2xl font-semibold text-white mt-12 mb-4 scroll-mt-20">
                    {children}
                  </h2>
                );
              },
              h3: ({ children }) => {
                const text = String(children);
                const id = slugify(text);
                return (
                  <h3 id={id} className="text-xl font-semibold text-white mt-8 mb-3 scroll-mt-20">
                    {children}
                  </h3>
                );
              },
              h4: ({ children }) => (
                <h4 className="text-lg font-semibold text-white mt-6 mb-2">{children}</h4>
              ),
              p: ({ children }) => (
                <p className="text-[15px] text-white/70 leading-relaxed mb-4">{children}</p>
              ),
              a: ({ href, children }) => {
                const isExternal = href?.startsWith("http");
                return (
                  <a
                    href={href}
                    target={isExternal ? "_blank" : undefined}
                    rel={isExternal ? "noopener noreferrer" : undefined}
                    className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2 decoration-indigo-400/30 hover:decoration-indigo-400 transition-colors"
                  >
                    {children}
                  </a>
                );
              },
              ul: ({ children }) => (
                <ul className="list-disc pl-6 space-y-2 mb-4 text-[15px] text-white/70">{children}</ul>
              ),
              ol: ({ children }) => (
                <ol className="list-decimal pl-6 space-y-2 mb-4 text-[15px] text-white/70">{children}</ol>
              ),
              li: ({ children }) => (
                <li className="leading-relaxed">{children}</li>
              ),
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-indigo-500/40 pl-4 my-6 text-white/60 italic">
                  {children}
                </blockquote>
              ),
              hr: () => <hr className="border-white/[0.08] my-10" />,
              strong: ({ children }) => (
                <strong className="font-semibold text-white">{children}</strong>
              ),
              em: ({ children }) => (
                <em className="text-white/80">{children}</em>
              ),
              img: ({ src, alt }) => (
                <img
                  src={src}
                  alt={alt ?? ""}
                  className="rounded-xl border border-border my-6 max-w-full"
                  loading="lazy"
                />
              ),
              table: ({ children }) => (
                <div className="overflow-x-auto my-6">
                  <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                    {children}
                  </table>
                </div>
              ),
              thead: ({ children }) => (
                <thead className="bg-surface-lighter text-white/70">{children}</thead>
              ),
              tbody: ({ children }) => (
                <tbody className="divide-y divide-border">{children}</tbody>
              ),
              tr: ({ children }) => (
                <tr className="hover:bg-white/[0.02] transition-colors">{children}</tr>
              ),
              th: ({ children }) => (
                <th className="text-left px-4 py-3 font-medium text-white/80">{children}</th>
              ),
              td: ({ children }) => (
                <td className="px-4 py-3 text-white/60">{children}</td>
              ),
              code: ({ className, children, ...props }) => {
                const match = /language-(\w+)/.exec(className || "");
                const isInline = !match;
                if (isInline) {
                  return (
                    <code className="text-sm bg-white/[0.06] text-indigo-300 px-1.5 py-0.5 rounded font-mono">
                      {children}
                    </code>
                  );
                }
                return (
                  <SyntaxHighlighter
                    style={oneDark}
                    language={match[1]}
                    PreTag="div"
                    customStyle={{
                      margin: "1.5rem 0",
                      borderRadius: "0.75rem",
                      fontSize: "0.8125rem",
                      border: "1px solid rgba(255,255,255,0.06)",
                    }}
                  >
                    {String(children).replace(/\n$/, "")}
                  </SyntaxHighlighter>
                );
              },
              pre: ({ children }) => <>{children}</>,
            }}
          >
            {markdown}
          </ReactMarkdown>
        </div>

        {/* Tags */}
        {post?.tags && post.tags.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mt-10 pt-8 border-t border-border">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs text-text-muted bg-surface-light px-2.5 py-1 rounded-lg border border-border"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Navigation */}
        <div className="mt-10 pt-8 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-4">
          {adjacent.prev ? (
            <Link
              to={`/blog/${adjacent.prev.slug}`}
              className="group p-4 rounded-xl border border-border hover:border-indigo-500/30 transition-all"
            >
              <span className="text-xs text-text-muted flex items-center gap-1 mb-1">
                <ArrowLeft className="w-3 h-3" /> Previous
              </span>
              <span className="text-sm font-medium text-white/80 group-hover:text-indigo-400 transition-colors line-clamp-2">
                {adjacent.prev.title}
              </span>
            </Link>
          ) : (
            <div />
          )}
          {adjacent.next && (
            <Link
              to={`/blog/${adjacent.next.slug}`}
              className="group p-4 rounded-xl border border-border hover:border-indigo-500/30 transition-all text-right"
            >
              <span className="text-xs text-text-muted flex items-center gap-1 justify-end mb-1">
                Next <ArrowRight className="w-3 h-3" />
              </span>
              <span className="text-sm font-medium text-white/80 group-hover:text-indigo-400 transition-colors line-clamp-2">
                {adjacent.next.title}
              </span>
            </Link>
          )}
        </div>

        {/* Back to blog */}
        <div className="mt-8 text-center">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            All posts
          </Link>
        </div>
      </article>
    </>
  );
}
